/**
 * Criterios de filtro del listado admin de empresas (compartido entre
 * listado y vista "Métricas y números").
 */
import { ESTADOS_EMPRESA } from './empresaStandards';
import { getPaginaWeb } from './empresaWebRedes';
import { getEmpresaMarcas } from './empresaMarcasHelper';
import { isValidCoordinate } from './geoValidation';

export const ESTADOS_FILTRO_ADMIN = [
  { label: 'Todos', value: 'todos' },
  { label: 'Catastro Inicial', value: 'catastro_inicial' },
  { label: 'Catalogada', value: ESTADOS_EMPRESA.CATALOGADA },
  { label: 'Ingresada', value: 'ingresada' },
  { label: 'Pendiente', value: ESTADOS_EMPRESA.PENDIENTE_VALIDACION },
  { label: 'En Visita', value: ESTADOS_EMPRESA.EN_VISITA },
  { label: 'Validada', value: ESTADOS_EMPRESA.VALIDADA },
  { label: 'Activa', value: ESTADOS_EMPRESA.ACTIVA },
  { label: 'Suspendida', value: ESTADOS_EMPRESA.SUSPENDIDA },
  { label: 'Inactiva', value: ESTADOS_EMPRESA.INACTIVA },
  { label: 'Rechazada', value: ESTADOS_EMPRESA.RECHAZADA }
];

export const FILTROS_EMPRESAS_ADMIN_INITIAL = {
  estado: 'todos',
  busqueda: '',
  tieneWeb: '',
  tieneLogoAssignado: '',
  qrGenerado: '',
  region: '',
  comuna: '',
  servicio: '',
  marca: '',
  latitud: '',
  rubro: '',
  tipoEmpresa: ''
};

export function empresaTieneQrPublicoGeneradoAdmin(empresa) {
  const u = empresa?.qrDownloadUrl;
  return typeof u === 'string' && u.trim().length > 0;
}

export function empresaTieneLatitudEnDocumentoAdmin(empresa) {
  if (!empresa || typeof empresa !== 'object') return false;
  const lat =
    empresa.latitud ??
    empresa.ubicacion?.lat ??
    empresa.coordenadas?.lat ??
    empresa.coordenadas_lat;
  return isValidCoordinate(lat, 'lat');
}

function getServiciosPreviewFilter(empresa) {
  const servicios = Array.isArray(empresa.servicios) ? empresa.servicios : [];
  if (!servicios.length) return [];
  return servicios
    .map((s) => (typeof s === 'string' ? s : s?.nombre || s?.id || ''))
    .filter(Boolean);
}

function getMarcasPreviewFilter(empresa) {
  const marcas = getEmpresaMarcas(empresa);
  if (!marcas.length) return [];
  return marcas.map((m) => (typeof m === 'string' ? m : m?.nombre || m?.id || String(m))).filter(Boolean);
}

/**
 * Misma lógica de texto que el buscador del listado admin (/admin/empresas):
 * nombre, emails, direcciones, categoría, rubro, descripciones, marcas y servicios (substring case-insensitive).
 * Incluye alias frecuentes en Firestore (nombre_empresa, direccion_empresa, razonSocial).
 * @param {object} empresa
 * @param {string} busqueda
 * @returns {boolean}
 */
export function empresaCoincideBusquedaListadoAdmin(empresa, busqueda) {
  const q = String(busqueda ?? '').trim();
  if (!q) return true;
  const ql = q.toLowerCase();
  const hay = (v) => String(v ?? '').toLowerCase().includes(ql);
  if (
    hay(empresa?.nombre) ||
    hay(empresa?.nombre_empresa) ||
    hay(empresa?.razonSocial) ||
    hay(empresa?.email) ||
    hay(empresa?.email_empresa) ||
    hay(empresa?.direccion) ||
    hay(empresa?.direccion_empresa) ||
    hay(empresa?.categoria) ||
    hay(empresa?.rubro) ||
    hay(empresa?.descripcion) ||
    hay(empresa?.descripcion_empresa)
  ) {
    return true;
  }
  return (
    getMarcasPreviewFilter(empresa).some((m) => String(m).toLowerCase().includes(ql)) ||
    getServiciosPreviewFilter(empresa).some((s) => String(s).toLowerCase().includes(ql))
  );
}

/**
 * @param {object} empresa
 * @param {typeof FILTROS_EMPRESAS_ADMIN_INITIAL} filtros
 */
export function empresaCumpleFiltrosListadoAdmin(empresa, filtros) {
  let matchEstado = filtros.estado === 'todos';
  if (filtros.estado === 'catastro_inicial') {
    matchEstado =
      empresa.estado === ESTADOS_EMPRESA.CATALOGADA || empresa.estado === ESTADOS_EMPRESA.INGRESADA;
  } else if (filtros.estado !== 'todos') {
    matchEstado = empresa.estado === filtros.estado;
  }

  const matchBusqueda = empresaCoincideBusquedaListadoAdmin(empresa, filtros.busqueda);

  const matchTieneWeb =
    filtros.tieneWeb === '' ||
    (filtros.tieneWeb === 'si' && getPaginaWeb(empresa)) ||
    (filtros.tieneWeb === 'no' && !getPaginaWeb(empresa));

  const matchTieneLogoAsignado =
    filtros.tieneLogoAssignado === '' ||
    (filtros.tieneLogoAssignado === 'si' && empresa.logoAsignado) ||
    (filtros.tieneLogoAssignado === 'no' && !empresa.logoAsignado);

  const tieneQr = empresaTieneQrPublicoGeneradoAdmin(empresa);
  const matchQrGenerado =
    filtros.qrGenerado === '' ||
    (filtros.qrGenerado === 'si' && tieneQr) ||
    (filtros.qrGenerado === 'no' && !tieneQr);

  const regionEmpresa = (empresa.region || empresa.region_empresa || '').trim();
  const matchRegion = !filtros.region
    ? true
    : filtros.region === '__sin_region__'
      ? !regionEmpresa
      : regionEmpresa.toLowerCase() === filtros.region.toLowerCase();

  const comunaEmpresa = (empresa.comuna || empresa.comuna_empresa || '').trim();
  const matchComuna = !filtros.comuna
    ? true
    : filtros.comuna === '__sin_comuna__'
      ? !comunaEmpresa
      : comunaEmpresa.toLowerCase() === filtros.comuna.toLowerCase();

  const serviciosEmpresa = getServiciosPreviewFilter(empresa);
  const matchServicio = !filtros.servicio
    ? true
    : filtros.servicio === '__sin_servicio__'
      ? serviciosEmpresa.length === 0
      : serviciosEmpresa.some(
          (s) => String(s).trim().toLowerCase() === String(filtros.servicio).trim().toLowerCase()
        );

  const marcasEmpresa = getMarcasPreviewFilter(empresa);
  const matchMarca = !filtros.marca
    ? true
    : filtros.marca === '__sin_marca__'
      ? marcasEmpresa.length === 0
      : marcasEmpresa.some(
          (m) => String(m).trim().toLowerCase() === String(filtros.marca).trim().toLowerCase()
        );

  const tieneLat = empresaTieneLatitudEnDocumentoAdmin(empresa);
  const matchLatitud =
    !filtros.latitud ||
    (filtros.latitud === 'con_datos' && tieneLat) ||
    (filtros.latitud === 'vacia' && !tieneLat);

  const rubroEmpresa = String(empresa.rubro || empresa.categoria || '').trim();
  const matchRubro =
    !filtros.rubro ||
    (filtros.rubro === '__sin_rubro__'
      ? !rubroEmpresa
      : rubroEmpresa.toLowerCase() === filtros.rubro.toLowerCase());

  const tipoEmp = String(empresa.tipoEmpresa || empresa.tipo_empresa || empresa.tipo || '').trim();
  const matchTipoEmpresa =
    !filtros.tipoEmpresa ||
    (filtros.tipoEmpresa === '__sin_tipo__'
      ? !tipoEmp
      : tipoEmp.toLowerCase() === filtros.tipoEmpresa.toLowerCase());

  return (
    matchEstado &&
    matchBusqueda &&
    matchTieneWeb &&
    matchTieneLogoAsignado &&
    matchQrGenerado &&
    matchRegion &&
    matchComuna &&
    matchServicio &&
    matchMarca &&
    matchLatitud &&
    matchRubro &&
    matchTipoEmpresa
  );
}
